import { formatarExameComCategoria } from 'data/requisicao';
import useIsMobile from 'hooks/useIsMobile';
import ModalProntuario from 'pages/laudo/prontuario/ModalProntuario';
import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Form, ListGroup, Row, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { selectDoctor } from 'redux/features/auth/authSlice';
import { gerarGuiaSadtIamspePdf } from 'services/guiaSadt';
import {
  createRequisicao,
  deleteRequisicao,
  getRequisicoesPaciente,
  updateRequisicao,
} from 'services/requisicao';
import useSWR from 'swr';
import { enviarArquivo, toWhatsappNumber } from 'utils/evolution';
import { mensagemErroExclusao, podeExcluirDocumento } from 'utils/gravacao';
import { blobToBase64 } from 'utils/pdfBlob';
import { generateRequisicaoPdf } from './generateRequisicaoPdf';
import SeletorExamesCheckbox from './SeletorExamesCheckbox';

export default function Requisicao({ prontuario }) {
  const history = useHistory();
  const isMobile = useIsMobile();
  const medico = useSelector(selectDoctor);
  const canMutate = !!medico;
  const swrKey = prontuario?.CODPAC ? ['requisicoes-paciente', prontuario?.CODPAC] : null;
  const [codRequisicaoSelecionada, setCodRequisicaoSelecionada] = useState(null);
  const [descricao, setDescricao] = useState('');
  const [mutating, setMutating] = useState(false);
  const [showModalProntuario, setShowModalProntuario] = useState(false);
  const [useIamspe, setUseIamspe] = useState(false);
  const {
    data: requisicoes = [],
    error,
    isLoading,
    mutate,
  } = useSWR(swrKey, ([, cod]) => getRequisicoesPaciente(cod), {
    shouldRetryOnError: false,
  });
  const shouldShowLoading = isLoading || mutating;
  const requisicaoSelecionada = useMemo(() => {
    if (!requisicoes.length) return null;
    if (codRequisicaoSelecionada == null) return requisicoes[0];
    return requisicoes.find((r) => r.CODREQUISICAO === codRequisicaoSelecionada) || requisicoes[0];
  }, [requisicoes, codRequisicaoSelecionada]);
  const isDefinitivo = requisicaoSelecionada?.GRAVACAO === 'DEFINITIVO';
  const podeExcluir =
    canMutate && requisicaoSelecionada && podeExcluirDocumento(requisicaoSelecionada.GRAVACAO);
  const isEditLocked = isDefinitivo || !canMutate;

  useEffect(() => {
    if (requisicaoSelecionada) {
      setDescricao(requisicaoSelecionada.DESCRICAO || '');
    }
  }, [requisicaoSelecionada?.CODREQUISICAO]);

  const selecionarUltimaRequisicao = (data) => {
    if (Array.isArray(data) && data.length > 0) {
      setCodRequisicaoSelecionada(data[0].CODREQUISICAO);
    }
  };

  const handleNovaRequisicao = async () => {
    if (!canMutate) {
      toast.warn('Apenas médicos podem criar requisições');
      return;
    }
    if (!prontuario || !medico) {
      toast.warn('Dados do paciente ou médico não disponíveis');
      return;
    }

    const convenio = prontuario.SIGLACONV || requisicoes[0]?.CONVENIO || '';
    const nome = prontuario.NOMEPAC || requisicoes[0]?.NOMEPAC || '';
    const cid = '';
    const indicacao = '';
    const cabecalho = `\n\nCONVÊNIO: ${convenio}\nNOME: ${nome}\nCID: ${cid}\nINDICAÇÃO: ${indicacao}\n\n`;
    const payload = {
      codConsulta: prontuario?.CODCONSULTA ?? null,
      codMedico: medico.codMed,
      codPaciente: prontuario.CODPAC,
      descricao: cabecalho,
      status: 'ONLINE',
      tipo: 'Requisição de Exame',
      gravacao: 'PARCIAL',
    };

    try {
      setMutating(true);
      await createRequisicao(payload);
      toast.success('Nova requisição criada com sucesso!');
      const updatedData = await mutate();
      selecionarUltimaRequisicao(updatedData);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao criar requisição');
    } finally {
      setMutating(false);
    }
  };

  const handleRepetirRequisicao = async () => {
    if (!canMutate || !medico) {
      toast.warn('Apenas médicos podem repetir requisições');
      return;
    }
    if (!requisicaoSelecionada) {
      toast.warn('Selecione uma requisição para repetir');
      return;
    }

    const payload = {
      codConsulta: prontuario?.CODCONSULTA ?? null,
      codMedico: medico.codMed,
      codPaciente: prontuario.CODPAC,
      descricao: requisicaoSelecionada?.DESCRICAO || '',
      status: 'ONLINE',
      tipo: requisicaoSelecionada?.TIPOREQUISICAO || 'Requisição de Exame',
      gravacao: 'PARCIAL',
    };

    try {
      setMutating(true);
      await createRequisicao(payload);
      toast.success('Requisição repetida com sucesso!');
      const updatedData = await mutate();
      selecionarUltimaRequisicao(updatedData);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao repetir requisição');
    } finally {
      setMutating(false);
    }
  };

  const handleAdicionarExames = (exames) => {
    if (!canMutate || isEditLocked) return;
    if (exames.length === 0) return;

    let textoAtualizado = descricao;
    exames.forEach((exame) => {
      const { novoTexto } = formatarExameComCategoria(exame, textoAtualizado);
      textoAtualizado = novoTexto;
    });

    setDescricao(textoAtualizado);
    toast.success(`${exames.length} exame(s) adicionado(s) à requisição!`);
  };

  const handleGravar = async (gravacao) => {
    if (!canMutate || !medico) {
      toast.warn('Apenas médicos podem gravar requisições');
      return;
    }
    if (!requisicaoSelecionada) {
      toast.warn('Nenhuma requisição selecionada');
      return;
    }

    const codAtual = requisicaoSelecionada.CODREQUISICAO;

    const payload = {
      descricao,
      tipo: requisicaoSelecionada?.TIPOREQUISICAO || 'Requisição de Exame',
      gravacao,
    };

    try {
      setMutating(true);
      await updateRequisicao(codAtual, payload);
      toast.success('Requisição gravada com sucesso!');
      await mutate(
        (prev = []) =>
          Array.isArray(prev)
            ? prev.map((item) =>
                item.CODREQUISICAO === codAtual
                  ? { ...item, DESCRICAO: descricao, GRAVACAO: gravacao }
                  : item
              )
            : prev,
        { revalidate: true }
      );
      setCodRequisicaoSelecionada(codAtual);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao gravar requisição');
    } finally {
      setMutating(false);
    }
  };

  const handleExcluir = async () => {
    if (!canMutate || !medico) {
      toast.warn('Apenas médicos podem excluir requisições');
      return;
    }
    if (!requisicaoSelecionada || !podeExcluirDocumento(requisicaoSelecionada.GRAVACAO)) {
      toast.warn('Requisição definitiva não pode ser excluída');
      return;
    }

    const confirmar = window.confirm(
      `Excluir a requisição #${requisicaoSelecionada.CODREQUISICAO}? Esta ação não pode ser desfeita.`
    );
    if (!confirmar) return;

    const cod = requisicaoSelecionada.CODREQUISICAO;
    try {
      setMutating(true);
      await deleteRequisicao(cod);
      const restantes = await mutate(
        (prev = []) =>
          Array.isArray(prev) ? prev.filter((item) => item.CODREQUISICAO !== cod) : prev,
        { revalidate: true }
      );
      setCodRequisicaoSelecionada(
        Array.isArray(restantes) && restantes[0] ? restantes[0].CODREQUISICAO : null
      );
      toast.success('Requisição excluída com sucesso.');
    } catch (err) {
      toast.error(mensagemErroExclusao(err, 'Erro ao excluir requisição'));
    } finally {
      setMutating(false);
    }
  };

  const handleImprimir = () => {
    if (!requisicaoSelecionada) {
      toast.warn('Selecione uma requisição para imprimir');
      return;
    }

    history.push({
      pathname: '/imprimir/requisicao',
      state: {
        requisicao: { ...requisicaoSelecionada, DESCRICAO: descricao },
        layout: useIamspe ? 'iamspe' : 'padrao',
        medico,
      },
    });
  };

  async function handleEnviarWhatsapp() {
    if (!requisicaoSelecionada || !descricao.trim()) {
      toast.warn('Selecione e grave uma requisição antes de enviar');
      return;
    }

    const celularWhats = toWhatsappNumber(
      requisicaoSelecionada?.CELULARPAC || prontuario?.CELULARPAC
    );

    try {
      const requisicaoParaPdf = { ...requisicaoSelecionada, DESCRICAO: descricao };

      const blob = useIamspe
        ? await gerarGuiaSadtIamspePdf({ requisicao: requisicaoParaPdf })
        : await generateRequisicaoPdf({ requisicao: requisicaoParaPdf });

      const pdfBase64 = await blobToBase64(blob);

      const payload = {
        to: celularWhats,
        pdfBase64,
        paciente: {
          id: requisicaoSelecionada?.CODPAC || prontuario?.CODPAC,
          nome: requisicaoSelecionada?.NOMEPAC || prontuario?.NOMEPAC,
          medico: medico?.apelido,
        },
        codLaudo: requisicaoSelecionada?.CODREQUISICAO,
        fileName: useIamspe
          ? `Guia-SADT-IAMSPE_${requisicaoSelecionada?.CODREQUISICAO || Date.now()}.pdf`
          : `Requisicao_${requisicaoSelecionada?.CODREQUISICAO || Date.now()}.pdf`,
        caption: `Ola *${requisicaoSelecionada?.NOMEPAC || prontuario?.NOMEPAC || 'Paciente'}*, tudo bem?\n\nSegue em anexo a sua *Requisição de Exames*.\n\nQualquer dúvida, nossa equipe estará à disposição no setor de atendimento: (14) 3402-2700`,
      };

      const result = await enviarArquivo(payload);

      toast.success(result.message);
    } catch (_error) {
      // Sentry aqui também!
      toast.error('Erro ao enviar por WhatsApp.');
    }
  }

  const handleAbrirAnamnese = () => {
    if (!prontuario) {
      toast.warn('Prontuário não encontrado para este paciente');
      return;
    }

    setShowModalProntuario(true);
  };

  return (
    <>
      {shouldShowLoading && (
        <div className="text-center mb-2">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {error ? (
        <Alert variant="danger">
          <p className="mb-0">{error?.response?.data?.message || 'Erro ao carregar requisições'}</p>
          <Button variant="outline-danger" size="sm" className="mt-3" onClick={() => mutate()}>
            Tentar novamente
          </Button>
        </Alert>
      ) : (
        <Row>
          <Col md={2} className="mb-3 mb-md-0">
            <ListGroup style={{ maxHeight: isMobile ? '215px' : '600px', overflowY: 'auto' }}>
              {requisicoes.map((req) => (
                <ListGroup.Item
                  key={req.CODREQUISICAO}
                  active={requisicaoSelecionada?.CODREQUISICAO === req.CODREQUISICAO}
                  onClick={() => setCodRequisicaoSelecionada(req.CODREQUISICAO)}
                  style={{
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    background:
                      requisicaoSelecionada?.CODREQUISICAO === req.CODREQUISICAO
                        ? '#0d6efd'
                        : '#fff',
                    color:
                      requisicaoSelecionada?.CODREQUISICAO === req.CODREQUISICAO
                        ? '#fff'
                        : '#212529',
                    border: '1px solid',
                    borderColor:
                      requisicaoSelecionada?.CODREQUISICAO === req.CODREQUISICAO
                        ? '#0d6efd'
                        : '#dee2e6',
                    transition: 'background 0.2s, border-color 0.2s, color 0.2s',
                  }}
                  onMouseOver={(e) => {
                    if (
                      !requisicaoSelecionada ||
                      requisicaoSelecionada?.CODREQUISICAO !== req.CODREQUISICAO
                    ) {
                      e.currentTarget.style.background = '#e9ecef';
                      e.currentTarget.style.borderColor = '#0d6efd';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (
                      !requisicaoSelecionada ||
                      requisicaoSelecionada?.CODREQUISICAO !== req.CODREQUISICAO
                    ) {
                      e.currentTarget.style.background = '#fff';
                      e.currentTarget.style.borderColor = '#dee2e6';
                    }
                  }}
                >
                  {req.dataFormatada}
                </ListGroup.Item>
              ))}
              {requisicoes.length === 0 && !shouldShowLoading && (
                <div className="text-center text-muted py-4">
                  <p>Nenhuma requisição encontrada</p>
                </div>
              )}
            </ListGroup>
          </Col>

          <Col md={10}>
            <div className="d-flex flex-wrap justify-content-between">
              <Form.Label className="font-weight-bold">
                Paciente: {prontuario?.NOMEPAC || ''}
              </Form.Label>
              <Form.Label className="font-weight-bold">
                Médico: {requisicaoSelecionada?.NOMEMED || medico?.nome || ''}
              </Form.Label>
            </div>

            <div
              style={{
                border: '1px solid #dee2e6',
                marginBottom: '10px',
                opacity: isEditLocked ? 0.6 : 1,
                pointerEvents: isEditLocked ? 'none' : 'auto',
              }}
            >
              <SeletorExamesCheckbox
                onConfirmarExames={handleAdicionarExames}
                disabled={isEditLocked}
              />
            </div>

            <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
              <div className="d-flex flex-wrap gap-2 w-100 w-md-auto">
                <Button
                  size="sm"
                  className="w-100 w-md-auto"
                  onClick={handleNovaRequisicao}
                  disabled={shouldShowLoading || !canMutate}
                >
                  Nova Requisição
                </Button>
                <Button
                  variant="info"
                  size="sm"
                  className="w-100 w-md-auto"
                  onClick={handleRepetirRequisicao}
                  disabled={shouldShowLoading || !requisicaoSelecionada || !canMutate}
                >
                  Repetir Requisição
                </Button>
                <Button
                  size="sm"
                  variant="success"
                  className="w-100 w-md-auto"
                  onClick={handleAbrirAnamnese}
                >
                  Anamnese
                </Button>
                {requisicaoSelecionada && (
                  <>
                    <small className="text-muted m-2">
                      Requisição {requisicaoSelecionada?.CODREQUISICAO} -{' '}
                      {requisicaoSelecionada?.dataFormatada}
                    </small>
                    <Badge
                      style={{
                        marginLeft: 10,
                        fontWeight: 600,
                        color: isDefinitivo ? '#198754' : '#ffc107',
                        background: isDefinitivo ? '#e9fbe7' : '#fffbe6',
                        borderRadius: 6,
                        padding: '2px 10px',
                        fontSize: 13,
                        border: isDefinitivo ? '1px solid #198754' : '1px solid #ffc107',
                      }}
                      className="d-inline-flex align-items-center m-1"
                      variant={
                        requisicaoSelecionada?.GRAVACAO === 'DEFINITIVO' ? 'success' : 'warning'
                      }
                    >
                      {requisicaoSelecionada?.GRAVACAO === 'DEFINITIVO' ? 'DEFINITIVO' : 'PARCIAL'}
                    </Badge>
                  </>
                )}
              </div>

              <label
                htmlFor="checkbox-iamspe"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '4px 10px',
                  border: `2px solid ${useIamspe ? '#0d6efd' : '#adb5bd'}`,
                  borderRadius: 6,
                  background: useIamspe ? '#e7f0ff' : '#f8f9fa',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  color: useIamspe ? '#0d6efd' : '#6c757d',
                  userSelect: 'none',
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap',
                }}
                title="Imprimir e Enviar usando o modelo da Guia SADT IAMSPE"
              >
                <Form.Check
                  type="checkbox"
                  id="checkbox-iamspe"
                  checked={useIamspe}
                  onChange={(e) => setUseIamspe(e.target.checked)}
                  style={{ margin: 0, cursor: 'pointer' }}
                />
                Modelo IAMSPE
              </label>
            </div>

            <div className="mb-1">
              <Form.Control
                as="textarea"
                rows={isMobile ? 10 : 15}
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Selecione os exames para adicionar à requisição"
                style={{ fontSize: '14px', marginTop: 10 }}
                disabled={isEditLocked}
              />
            </div>

            <div className="d-flex flex-wrap justify-content-center gap-2">
              <Button
                className="w-100 w-md-auto"
                variant="outline-success"
                onClick={() => handleGravar('PARCIAL')}
                disabled={shouldShowLoading || !requisicaoSelecionada || isDefinitivo || !canMutate}
              >
                Gravar Parcial
              </Button>
              <Button
                className="w-100 w-md-auto"
                variant="outline-success"
                onClick={() => handleGravar('DEFINITIVO')}
                disabled={shouldShowLoading || !requisicaoSelecionada || isDefinitivo || !canMutate}
              >
                Gravar Definitivo
              </Button>
              {podeExcluir && (
                <Button
                  className="w-100 w-md-auto"
                  variant="outline-danger"
                  onClick={handleExcluir}
                  disabled={shouldShowLoading}
                >
                  Excluir
                </Button>
              )}
              <Button
                className="w-100 w-md-auto"
                variant="warning"
                onClick={() => handleImprimir()}
                disabled={!requisicaoSelecionada || !isDefinitivo}
              >
                Timbrado
              </Button>
              <Button
                className="w-100 w-md-auto"
                onClick={handleEnviarWhatsapp}
                disabled={!requisicaoSelecionada || !descricao.trim() || !isDefinitivo}
                variant="outline-success"
              >
                <i className="bi bi-whatsapp" style={{ color: '#25d366', marginRight: 6 }} /> Enviar
              </Button>
            </div>
          </Col>
        </Row>
      )}

      <ModalProntuario
        show={showModalProntuario}
        onHide={() => setShowModalProntuario(false)}
        prontuario={prontuario}
      />
    </>
  );
}
